import produce from 'immer'
import { CoverageType } from 'src/lib/types/discounts'
import { CatalogueCategoryType, CatalogueDataType } from 'src/lib/types/common/catalogue'
import {
  JourneyStepType,
  UpSellFormStateType,
  UpsellAnalyticsProductCombinationType,
  UpsellAnalyticsProductType,
  UpsellAnalyticsWidgetSet,
  UpsellAnalyticsWidgetType,
  UpSellTableDataType,
  UpsellDetailsTableRowType,
  UpsellEntitledProductType,
  UpsellEntitledCollectionType,
  WidgetProductType,
  UpsellDiscountType
} from '../types/upsell'
import { getEnabledChannelsArray } from './parsers'
import { analyticsApiRequestType, apiURI, MetricComparisonStatus } from 'src/lib/utils/constants'
import { FilterConfigType } from '../types/filters'
import { extractDateFromFilters } from './analyticsHelpers'
import { AnalyticsMetricType, GenericAnalyticsWidgetUIConfigType } from '../types/analytics'
import { errorToast, successToast } from './toasters'
import { numberWithCommas, truncateString } from './helpers'
import {
  ActiveUpsellApi,
  TooManyActiveUpsellsErrorData,
  UpsellDegradeDialogData,
  UpsellPlanId,
  UpsellTableData
} from '../types/appsIntegrations'
import { successNudgeToast } from 'src/components/common/toaster/SuccessNudgeToast'

const issuerId = process.env.NEXT_PUBLIC_APP_ISSUER_ID ?? ''

export const mapRadioCatalogueCatagory = (selectedOption: CoverageType): CatalogueCategoryType => {
  switch (selectedOption) {
    case 'COLLECTION':
      return 'COLLECTION'
    default:
      return 'PRODUCT_AND_VARIANT'
  }
}

export const handleUpsellDiscountTypeClick = ({
  setUpSellFormState,
  itemType,
  itemId,
  optionIndex,
  upsellDiscountTypeOptions
}: {
  setUpSellFormState: (updater: any) => void
  itemType: 'product' | 'collection'
  itemId: string
  optionIndex: number
  upsellDiscountTypeOptions: Array<{ value: UpsellDiscountType['type'] }>
}) => {
  setUpSellFormState(
    produce<UpSellFormStateType>(draft => {
      if (itemType === 'product') {
        const items = draft.entitlementsConfig?.entitledItemsConfig?.items
        if (!items) return

        const itemIndex = items.findIndex(existingItem => existingItem.productId === itemId)
        if (itemIndex !== -1) {
          const selectedType = upsellDiscountTypeOptions?.[optionIndex]?.value
          items[itemIndex].productDiscount.type = selectedType
          if (selectedType === 'NONE') {
            items[itemIndex].productDiscount.concessionAmount = 0
          }
        }
      } else {
        const items = draft.entitlementsConfig?.entitledCollectionsConfig?.items
        if (!items) return

        const itemIndex = items.findIndex(existingItem => existingItem.collectionId === itemId)
        if (itemIndex !== -1) {
          const selectedType = upsellDiscountTypeOptions?.[optionIndex]?.value
          items[itemIndex].collectionDiscount.type = selectedType
          if (selectedType === 'NONE') {
            items[itemIndex].collectionDiscount.concessionAmount = 0
          }
        }
      }
    })
  )
}

export const handleUpsellDiscountAmountChange = ({
  setUpSellFormState,
  itemType,
  itemId,
  rawValue
}: {
  setUpSellFormState: (updater: any) => void
  itemType: 'product' | 'collection'
  itemId: string
  rawValue: string
}) => {
  setUpSellFormState(
    produce<UpSellFormStateType>(draft => {
      if (itemType === 'product') {
        const items = draft.entitlementsConfig?.entitledItemsConfig?.items
        if (!items) return

        const itemIndex = items.findIndex(existingItem => existingItem.productId === itemId)
        if (itemIndex !== -1) {
          items[itemIndex].productDiscount.concessionAmount = Math.max(0, Math.min(99, parseFloat(rawValue)))
        }
      } else {
        const items = draft.entitlementsConfig?.entitledCollectionsConfig?.items
        if (!items) return

        const itemIndex = items.findIndex(existingItem => existingItem.collectionId === itemId)
        if (itemIndex !== -1) {
          items[itemIndex].collectionDiscount.concessionAmount = Math.max(0, Math.min(99, parseFloat(rawValue)))
        }
      }
    })
  )
}

export const createUpSellPayload = (upSellStateObject: UpSellFormStateType, isEditMode: boolean) => {
  const getPrerequisiteItems = () => {
    switch (upSellStateObject?.prerequisitesConfig?.valueType) {
      case 'ALL':
        return {
          prerequisite_product_ids: isEditMode ? [] : null,
          prerequisite_collection_ids: isEditMode ? [] : null
        }
      case 'PRODUCT':
        return {
          prerequisite_product_ids: upSellStateObject?.prerequisitesConfig?.prerequisiteItemsConfig?.items,
          prerequisite_collection_ids: isEditMode ? [] : null
        }
      case 'COLLECTION':
        return {
          prerequisite_collection_ids: upSellStateObject?.prerequisitesConfig?.prerequisiteItemsConfig?.items,
          prerequisite_product_ids: isEditMode ? [] : null
        }
    }
  }

  const entitledProductsFromProducts =
    upSellStateObject?.entitlementsConfig?.entitledItemsConfig?.items?.map(
      (item: UpsellEntitledProductType, index: number) => {
        const catalogueItem = upSellStateObject?.entitlementsConfig?.entitledItemsConfig?.catalogueMetadata?.find(
          (cat: any) => cat.id === item.productId
        )

        const allVariantsSelected = catalogueItem?.allVariantsSelected === true

        if (allVariantsSelected) {
          return {
            product_id: item?.productId,
            discount: {
              concession_amount: item?.productDiscount?.concessionAmount,
              type: 'PERCENTAGE'
            },
            priority: index + 1
          }
        } else {
          const variantIds: string[] = catalogueItem?.variants?.map((v: CatalogueDataType) => v.id as string) || []
          return {
            product_id: item?.productId,
            variant_ids: variantIds,
            discount: {
              concession_amount: item?.productDiscount?.concessionAmount,
              type: 'PERCENTAGE'
            },
            priority: index + 1
          }
        }
      }
    ) || []

  const entitledProductsFromCollections =
    upSellStateObject?.entitlementsConfig?.entitledCollectionsConfig?.items?.map(
      (item: UpsellEntitledCollectionType, index: number) => {
        return {
          collection_id: item?.collectionId,
          discount: {
            concession_amount: item?.collectionDiscount?.concessionAmount,
            type: 'PERCENTAGE'
          },
          priority: entitledProductsFromProducts.length + index + 1
        }
      }
    ) || []

  const entitledProducts = [...entitledProductsFromProducts, ...entitledProductsFromCollections]

  return {
    name: upSellStateObject?.name?.trim(),
    enabled_channels: getEnabledChannelsArray(upSellStateObject?.channels),
    entitled_products: entitledProducts,
    ...(upSellStateObject?.prerequisitesConfig?.valueType === 'PRODUCT'
      ? { prerequisite_variant_ids: upSellStateObject?.prerequisitesConfig?.prerequisiteItemsConfig?.variants }
      : { prerequisite_variant_ids: isEditMode ? [] : null }),
    ...(upSellStateObject?.prerequisitesConfig?.valueType !== 'ALL'
      ? getPrerequisiteItems()
      : { prerequisite_product_ids: isEditMode ? [] : null, prerequisite_collection_ids: isEditMode ? [] : null }),
    real_estate: upSellStateObject?.realEstate,
    metadata: {
      entitledCatalogue: [
        ...(upSellStateObject?.entitlementsConfig?.entitledItemsConfig?.catalogueMetadata || []),
        ...(upSellStateObject?.entitlementsConfig?.entitledCollectionsConfig?.catalogueMetadata || [])
      ],
      prerequisiteCatalogue: upSellStateObject?.prerequisitesConfig?.prerequisiteItemsConfig?.catalogueMetadata
    }
  }
}

export const checkValidCase = (upSellStateObject: UpSellFormStateType, step: JourneyStepType | 'ALL') => {
  const isSetupStepValid = (): boolean => {
    if (upSellStateObject?.name?.trim()?.length === 0) {
      errorToast('Please enter a valid name for the upsell')
      return false
    }

    const productsLength = upSellStateObject?.entitlementsConfig?.entitledItemsConfig?.items?.length ?? 0
    const collectionsLength = upSellStateObject?.entitlementsConfig?.entitledCollectionsConfig?.items?.length ?? 0

    if (productsLength && collectionsLength) {
      errorToast('Please select either products or collections, not both')
      return false
    }

    const totalItems = productsLength + collectionsLength

    if (totalItems === 0) {
      errorToast('Please select at least one entitled item')
      return false
    }

    if (totalItems <= 1) {
      errorToast('Please select at least two entitled items')
      return false
    }
    if (totalItems >= 6) {
      errorToast('Please select no more than five entitled items')
      return false
    }

    let isDiscountValid = true
    upSellStateObject?.entitlementsConfig?.entitledItemsConfig?.items?.forEach((item: UpsellEntitledProductType) => {
      if (item?.productDiscount?.type === 'PERCENTAGE' && (item?.productDiscount?.concessionAmount || 0) <= 0) {
        errorToast('Discount value for product cannot be less than or equal to 0')
        isDiscountValid = false
      }
    })
    upSellStateObject?.entitlementsConfig?.entitledCollectionsConfig?.items?.forEach(
      (item: UpsellEntitledCollectionType) => {
        if (item?.collectionDiscount?.type === 'PERCENTAGE' && (item?.collectionDiscount?.concessionAmount || 0) <= 0) {
          errorToast('Discount value for collection cannot be less than or equal to 0')
          isDiscountValid = false
        }
      }
    )
    if (!isDiscountValid) {
      return false
    }
    return true
  }

  const isConditionStepValid = (): boolean => {
    if (
      upSellStateObject?.prerequisitesConfig?.valueType !== 'ALL' &&
      !upSellStateObject?.prerequisitesConfig?.prerequisiteItemsConfig?.items?.length &&
      !upSellStateObject?.prerequisitesConfig?.prerequisiteItemsConfig?.variants?.length
    ) {
      errorToast('Please select at least one prerequisite item or variant')
      return false
    }
    if (!getEnabledChannelsArray(upSellStateObject?.channels)?.length) {
      errorToast('Please select at least one channel')
      return false
    }
    return true
  }

  switch (step) {
    case 'setup':
      return isSetupStepValid()
    case 'condition':
      return isSetupStepValid() && isConditionStepValid()
    default:
      return isSetupStepValid() && isConditionStepValid()
  }
}

export const parseGenericWidgetData = (
  responses: Array<any>,
  uiConfig: GenericAnalyticsWidgetUIConfigType
): GenericAnalyticsWidgetUIConfigType => {
  return {
    ...uiConfig,
    ...(uiConfig?.primaryMetricList
      ? {
          primaryMetricList: uiConfig?.primaryMetricList?.map(
            (primaryMetric: AnalyticsMetricType, index: number): AnalyticsMetricType => {
              return {
                ...primaryMetric,
                value: Boolean(responses?.[index]?.interval_response?.value)
                  ? responses?.[index]?.interval_response?.value
                  : '0',
                compareStatus:
                  responses?.[index]?.comparison_response?.comparison === 'DECREASE'
                    ? MetricComparisonStatus.DECREASED
                    : responses?.[index]?.comparison_response?.comparison === 'INCREASE'
                    ? MetricComparisonStatus.INCREASED
                    : MetricComparisonStatus.EQUAL,
                comparePercentage: Math.abs(responses?.[index]?.comparison_response?.difference),
                compareValue: responses?.[index]?.comparison_interval_response?.value
              }
            }
          )
        }
      : {}),
    ...(uiConfig?.secondaryMetricsList
      ? {
          secondaryMetricsList: uiConfig?.secondaryMetricsList?.map(
            (secondaryMetric: AnalyticsMetricType, index: number): AnalyticsMetricType => {
              return {
                ...secondaryMetric,
                value: Boolean(responses?.[index]?.interval_response?.value)
                  ? responses?.[index]?.interval_response?.value
                  : '0',
                compareStatus:
                  responses?.[index]?.comparison_response?.comparison === 'DECREASE'
                    ? MetricComparisonStatus.DECREASED
                    : responses?.[index]?.comparison_response?.comparison === 'INCREASE'
                    ? MetricComparisonStatus.INCREASED
                    : MetricComparisonStatus.EQUAL,
                comparePercentage: Math.abs(responses?.[index]?.comparison_response?.difference),
                compareValue: responses?.[index]?.comparison_interval_response?.value
              }
            }
          )
        }
      : {})
  }
}

export const parseUpsellAnalyticsProduct = (response: any): UpsellAnalyticsProductType => {
  return {
    id: response?.product_id,
    name: response?.name,
    description: response?.description,
    imageURL: response?.images?.find((image: any) => image?.is_primary)?.src ?? response?.images?.[0]?.src,
    type: response?.product_type,
    variants: response?.variants
  }
}

export const parseUpsellProductCombinations = (response: Array<any>): UpsellAnalyticsProductCombinationType[] => {
  return response?.map((data: any, index: number) => {
    return {
      originalProduct: parseUpsellAnalyticsProduct(data?.original_product),
      upSoldProduct: parseUpsellAnalyticsProduct(data?.upsold_product),
      timesSold: data?.number_of_times_sold
    }
  })
}

export const parseUpsellProductDistribution = (
  response: any,
  uiConfig: GenericAnalyticsWidgetUIConfigType
): GenericAnalyticsWidgetUIConfigType => {
  const currentPeriodData = response?.[0]
  const comparisonPeriodData = response?.[1]
  if (comparisonPeriodData) {
    const currentTotalProduct = Number(currentPeriodData?.totalProduct ?? 0)
    const previousTotalProduct = Number(comparisonPeriodData?.totalProduct ?? 0)
    const currentTotalMoney = Number(currentPeriodData?.totalMoney ?? 0)
    const previousTotalMoney = Number(comparisonPeriodData?.totalMoney ?? 0)
    return {
      ...uiConfig,
      ...(uiConfig?.lineMetricList
        ? {
            lineMetricList: uiConfig?.lineMetricList?.map(
              (lineMetric: AnalyticsMetricType, index: number): AnalyticsMetricType => {
                return {
                  ...lineMetric,
                  value: {
                    distributionItems:
                      currentPeriodData?.results?.map((widgetProduct: WidgetProductType, index: number) => {
                        const title = widgetProduct?.title ?? 'Unknown'
                        const orderCount = Number(widgetProduct?.order_count ?? 0)
                        return {
                          share: orderCount,
                          title: title,
                          label: (
                            <div className='flex flex-col justify-start items-center gap-0.5 overflow-hidden leading-3'>
                              <p className='text-coal-dark text-sm font-medium w-full overflow-hidden text-ellipsis'>
                                {truncateString(title, 16)}
                              </p>
                              <p className='text-coal-dark text-sm font-medium w-full overflow-hidden text-ellipsis'>
                                {numberWithCommas(orderCount.toString(), 0) + ' units'}
                              </p>
                            </div>
                          )
                        }
                      }) || []
                  }
                }
              }
            )
          }
        : {}),
      ...(uiConfig?.secondaryMetricsList
        ? {
            secondaryMetricsList: uiConfig?.secondaryMetricsList?.map(
              (secondaryMetric: AnalyticsMetricType, index: number): AnalyticsMetricType => {
                const price = Number(response?.[0]?.totalMoney ?? 0)
                const updatedMetric = {
                  ...secondaryMetric,
                  value:
                    secondaryMetric.title === 'Product sold via upsell'
                      ? response?.[0]?.totalProduct ?? 0
                      : `₹${price.toLocaleString('en-IN')}`
                }

                if (secondaryMetric.title === 'Product sold via upsell') {
                  updatedMetric.compareStatus = !comparisonPeriodData
                    ? MetricComparisonStatus.EQUAL
                    : currentTotalProduct > previousTotalProduct
                    ? MetricComparisonStatus.INCREASED
                    : currentTotalProduct < previousTotalProduct
                    ? MetricComparisonStatus.DECREASED
                    : MetricComparisonStatus.EQUAL

                  updatedMetric.comparePercentage = Number.isFinite(
                    ((currentTotalProduct - previousTotalProduct) / previousTotalProduct) * 100
                  )
                    ? Number(
                        Math.abs(((currentTotalProduct - previousTotalProduct) / previousTotalProduct) * 100).toFixed(2)
                      )
                    : 0

                  updatedMetric.compareValue = previousTotalProduct
                }

                if (secondaryMetric.title === 'Revenue from upsell') {
                  updatedMetric.compareStatus = !comparisonPeriodData
                    ? MetricComparisonStatus.EQUAL
                    : currentTotalMoney > previousTotalMoney
                    ? MetricComparisonStatus.INCREASED
                    : currentTotalMoney < previousTotalMoney
                    ? MetricComparisonStatus.DECREASED
                    : MetricComparisonStatus.EQUAL

                  updatedMetric.comparePercentage = Number.isFinite(
                    ((currentTotalMoney - previousTotalMoney) / previousTotalMoney) * 100
                  )
                    ? Number(Math.abs(((currentTotalMoney - previousTotalMoney) / previousTotalMoney) * 100).toFixed(2))
                    : 0

                  updatedMetric.compareValue = previousTotalMoney
                }

                return updatedMetric
              }
            )
          }
        : {})
    }
  }
  return {
    ...uiConfig,
    ...(uiConfig?.lineMetricList
      ? {
          lineMetricList: uiConfig?.lineMetricList?.map(
            (lineMetric: AnalyticsMetricType, index: number): AnalyticsMetricType => {
              return {
                ...lineMetric,
                value: {
                  distributionItems:
                    currentPeriodData?.results?.map((widgetProduct: WidgetProductType, index: number) => {
                      const title = widgetProduct?.title ?? 'Unknown'
                      const orderCount = Number(widgetProduct?.order_count ?? 0)
                      return {
                        share: orderCount,
                        title: title,
                        label: (
                          <div className='flex flex-col justify-start items-center gap-0.5 overflow-hidden leading-3'>
                            <p className='text-coal-dark text-sm font-medium w-full overflow-hidden text-ellipsis'>
                              {truncateString(title, 16)}
                            </p>
                            <p className='text-coal-dark text-sm font-medium w-full overflow-hidden text-ellipsis'>
                              {numberWithCommas(orderCount.toString(), 0) + ' units'}
                            </p>
                          </div>
                        )
                      }
                    }) || []
                }
              }
            }
          )
        }
      : {}),
    ...(uiConfig?.secondaryMetricsList
      ? {
          secondaryMetricsList: uiConfig?.secondaryMetricsList?.map(
            (secondaryMetric: AnalyticsMetricType, index: number): AnalyticsMetricType => {
              const price = Number(response?.[0]?.totalMoney ?? 0)
              return {
                ...secondaryMetric,
                value:
                  secondaryMetric.title === 'Product sold via upsell'
                    ? currentPeriodData?.totalProduct
                    : `₹${price.toLocaleString('en-IN')}`
              }
            }
          )
        }
      : {})
  }
}

export const UpsellAnalyticsConfig = (merchantId: string, filters: FilterConfigType): UpsellAnalyticsWidgetType[] => {
  const payload = {
    baseURL: apiURI.CHECKOUT_NODE ?? '',
    requestType: analyticsApiRequestType.POST,
    url: `/dashboard/widget/upsell-popular-combinations`,
    payload: {
      merchantId: merchantId,
      issuerId: issuerId,
      timeRange: {
        start: extractDateFromFilters(filters, 'startDate', 'UTC'),
        end: extractDateFromFilters(filters, 'endDate', 'UTC')
      },
      widgetId: 'upsell_popular_combinations',
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      channel: 'web'
    }
  }
  const queryParams = `?timeZone=Asia%2FCalcutta&startDate=${extractDateFromFilters(
    filters,
    'startDate'
  )}&endDate=${extractDateFromFilters(filters, 'endDate')}`
  const comparisonParams = `?timeZone=Asia%2FCalcutta&startDate=${extractDateFromFilters(
    filters,
    'compareToStartDate'
  )}&endDate=${extractDateFromFilters(filters, 'compareToEndDate')}`
  const analyticsURL = `/dashboard/widget/upsell/upsell_orders`
  return [
    {
      type: UpsellAnalyticsWidgetSet.GENERIC_WIDGET,
      data: {
        uiConfig: {
          id: 'upsell-analytics',
          positionX: 0,
          orientation: 'VERTICAL',
          positionY: 0,
          title: 'Popular products in upsell',
          lineMetricList: [
            {
              title: 'Popular products in upsell',
              header: 'Popular products in upsell',
              type: 'LINE_DISTRIBUTION_CONFIG_TYPE',
              value: []
            }
          ],
          secondaryMetricsList: [
            {
              title: 'Product sold via upsell',
              header: 'Total number of products sold via upsell',
              type: 'NUMBER',
              value: 0
            },
            {
              title: 'Revenue from upsell',
              header: 'Total revenue from upsell',
              type: 'STRING',
              value: '0'
            }
          ]
        },
        requestConfig: {
          endpoints:
            comparisonParams && filters.comparison !== 'none'
              ? [
                  {
                    baseURL: apiURI.CHECKOUT_NODE ?? '',
                    requestType: analyticsApiRequestType.GET,
                    url: `${analyticsURL}${queryParams}`
                  },
                  {
                    baseURL: apiURI.CHECKOUT_NODE ?? '',
                    requestType: analyticsApiRequestType.GET,
                    url: `${analyticsURL}${comparisonParams}`
                  }
                ]
              : [
                  {
                    baseURL: apiURI.CHECKOUT_NODE ?? '',
                    requestType: analyticsApiRequestType.GET,
                    url: `${analyticsURL}${queryParams}`
                  }
                ],
          filters: filters,
          parser: parseUpsellProductDistribution
        }
      }
    },
    {
      type: UpsellAnalyticsWidgetSet.PRODUCT_COMBINATION_WIDGET,
      data: {
        combinations: [],
        requestConfig: {
          endpoints: [payload],
          filters: filters,
          parser: parseUpsellProductCombinations
        }
      }
    }
    // {
    //   type: UpsellAnalyticsWidgetSet.GENERIC_WIDGET,
    //   data: {
    //     uiConfig: {
    //       id: 'aov-with-upsell',
    //       positionX: 0,
    //       positionY: 2,
    //       title: '',
    //       primaryMetricList: [
    //         {
    //           title: 'AOV when upsell product is added',
    //           header: 'AOV with upsell',
    //           type: 'AMOUNT',
    //           value: 0
    //         },
    //         {
    //           title: 'Change in AOV with upsell',
    //           header: 'AOV change rate',
    //           type: 'PERCENTAGE',
    //           value: 0
    //         }
    //       ]
    //     },
    //     requestConfig: {
    //       endpoints: [
    //         {
    //           baseURL: apiURI.DISCOUNT_ENGINE ?? '',
    //           requestType: analyticsApiRequestType.POST,
    //           url: `${baseURL}/average-order-value`,
    //           payload: payload
    //         },
    //         {
    //           baseURL: apiURI.DISCOUNT_ENGINE ?? '',
    //           requestType: analyticsApiRequestType.POST,
    //           url: `${baseURL}/upsell-aov-increase-rate`,
    //           payload: payload
    //         }
    //       ],
    //       filters: filters,
    //       parser: parseGenericWidgetData
    //     }
    //   }
    // },
    // {
    //   type: UpsellAnalyticsWidgetSet.GENERIC_WIDGET,
    //   data: {
    //     uiConfig: {
    //       id: 'aov-with-upsell',
    //       positionX: 1,
    //       positionY: 2,
    //       title: 'Upsell contribution rate',
    //       primaryMetricList: [
    //         {
    //           title: 'Upsell contribution in relation to the order amount',
    //           type: 'PERCENTAGE',
    //           value: 0
    //         }
    //       ]
    //     },
    //     requestConfig: {
    //       endpoints: [
    //         {
    //           baseURL: apiURI.DISCOUNT_ENGINE ?? '',
    //           requestType: analyticsApiRequestType.POST,
    //           url: `${baseURL}/upsell-rate`,
    //           payload: payload
    //         }
    //       ],
    //       filters: filters,
    //       parser: parseGenericWidgetData
    //     }
    //   }
    // },
    // {
    //   type: UpsellAnalyticsWidgetSet.GENERIC_WIDGET,
    //   data: {
    //     uiConfig: {
    //       id: 'aov-with-upsell',
    //       positionX: 1,
    //       positionY: 2,
    //       title: 'Average number of items in cart',
    //       secondaryMetricsList: [
    //         {
    //           title: 'With upsell',
    //           type: 'NUMBER',
    //           value: 0
    //         },
    //         {
    //           title: 'Without upsell',
    //           type: 'NUMBER',
    //           value: 0
    //         }
    //       ]
    //     },
    //     requestConfig: {
    //       endpoints: [
    //         {
    //           baseURL: apiURI.DISCOUNT_ENGINE ?? '',
    //           requestType: analyticsApiRequestType.POST,
    //           url: `${baseURL}/average-products-sold-with-upsell`,
    //           payload: payload
    //         },
    //         {
    //           baseURL: apiURI.DISCOUNT_ENGINE ?? '',
    //           requestType: analyticsApiRequestType.POST,
    //           url: `${baseURL}/average-products-sold-without-upsell`,
    //           payload: payload
    //         }
    //       ],
    //       filters: filters,
    //       parser: parseGenericWidgetData
    //     }
    //   }
    // }
  ]
}

export const getUpsellDetailsRows = (upsellData: UpSellTableDataType): UpsellDetailsTableRowType[] => {
  const productRows: UpsellDetailsTableRowType[] = upsellData.entitledProducts.map(
    (upsellProduct: UpsellEntitledProductType, index: number) => {
      const metadataItem = upsellData?.metadata?.entitledCatalogue?.find(
        (item: any) => item?.id === upsellProduct.productId
      )
      return {
        id: upsellProduct.productId,
        productName: Boolean(metadataItem?.description) ? metadataItem?.description : '...',
        productImage: Boolean(metadataItem?.imageUrl) ? metadataItem?.imageUrl : '...',
        offer: upsellProduct.productDiscount,
        itemType: 'PRODUCT' as const
      }
    }
  )

  const collectionRows: UpsellDetailsTableRowType[] = (upsellData.entitledCollections || []).map(
    (upsellCollection: UpsellEntitledCollectionType, index: number) => {
      const metadataItem = upsellData?.metadata?.entitledCatalogue?.find(
        (item: any) => item?.id === upsellCollection.collectionId
      )
      return {
        id: upsellCollection.collectionId,
        productName: Boolean(metadataItem?.description) ? metadataItem?.description : '...',
        productImage: Boolean(metadataItem?.imageUrl) ? metadataItem?.imageUrl : '...',
        offer: upsellCollection.collectionDiscount,
        itemType: 'COLLECTION' as const
      }
    }
  )

  return [...productRows, ...collectionRows]
}

export function joinData(rows: any, response: any) {
  const productMaps = new Map<
    string,
    { [productId: string]: { viewCount: number; addedCount: number; purchasedCount: number } }
  >()
  response?.data?.widgetData?.forEach(({ group_id, distribution }: any) => {
    distribution.forEach(({ product_id, viewed, added, purchased }: any) => {
      if (!productMaps.has(group_id)) {
        productMaps.set(group_id, {
          [product_id]: { viewCount: 0, addedCount: 0, purchasedCount: 0 }
        })
      }

      const existingMetrics = productMaps.get(group_id)?.[product_id] || {
        viewCount: 0,
        addedCount: 0,
        purchasedCount: 0
      }

      productMaps.get(group_id)![product_id] = {
        viewCount: existingMetrics.viewCount + viewed,
        addedCount: existingMetrics.addedCount + added,
        purchasedCount: existingMetrics.purchasedCount + purchased
      }
    })
  })

  return rows.map((row: any) => {
    const groupData = response?.data?.widgetData?.find((data: any) => data.group_id === row.id)
    const groupConversion = groupData ? groupData.group_conversion.toFixed(2) : 0

    const entitledProducts = row.entitledProducts.map((product: any) => {
      const groupMetrics = productMaps.get(groupData?.group_id)
      const productInfo = groupMetrics ? groupMetrics[product.productId] : {}

      return {
        ...product,
        ...productInfo
      }
    })

    return {
      ...row,
      groupConversion,
      entitledProducts
    }
  })
}

export const joinUpdatedRows = (rows: any, upsellTableData: UpSellTableDataType[]) => {
  const updatedIds = new Set(upsellTableData.map(upsell => upsell.id))
  const existingRows = rows.filter((row: any) => !updatedIds.has(row.id))

  return [...existingRows, ...upsellTableData]
}

export const checkIfAnyProductHasDiscount = (products: UpsellEntitledProductType[]) => {
  return products.some(
    product => product.productDiscount?.type === 'PERCENTAGE' && (product.productDiscount?.concessionAmount || 0) > 0
  )
}

const toUpsellTableData = (raw: ActiveUpsellApi): UpsellTableData => ({
  id: raw.id,
  title: raw.name,
  // First catalogue item isn't guaranteed to have an image — use the first one that does.
  image_url: raw.metadata?.entitledCatalogue?.find(item => item.imageUrl)?.imageUrl ?? '',
  created_at: new Date(raw.created_at).toISOString(),
  is_active: raw.is_active
})

/**
 * Reads the FSE_001 (too many active upsells) failure from a tier-update error — the body sits on
 * `error.response.data` both for thrown axios errors and for unwrapSettlementV2's success:false errors.
 */
export const parseUpsellDegradeErrorData = (error: unknown): UpsellDegradeDialogData => {
  const data = (error as { response?: { data?: { error?: { data?: TooManyActiveUpsellsErrorData } } } })?.response?.data
    ?.error?.data

  return {
    upsells: (data?.active_upsells ?? []).map(toUpsellTableData)
  }
}

// The one success message for an Upsell tier change, wherever it happens — moving to Plus gets the +Plus nudge, any other move (e.g. Plus → Lite) the plain toast. Callers show it after a successful updateTier.
export const notifyUpsellTierUpdated = (planId: UpsellPlanId) => {
  if (planId === 'plus') {
    successNudgeToast({
      title: 'You’re now on +Plus!',
      description: 'Your plan has been upgraded successfully. All +Plus features are now unlocked.'
    })
  } else {
    successToast('Plan updated.')
  }
}
